import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Smartphone, Bell, CheckCircle, Settings, Zap, Send } from 'lucide-react';

const SmsNotificationsGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <MessageSquare className="text-green-600" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Text Messages (SMS)</h1>
              <p className="text-lg text-gray-600">Get instant alerts, and text quotes and invoices to your customers</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <Zap className="text-green-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Two Ways SuddyFlow Uses Text Messages</h2>
              <p className="text-gray-700 mb-3">
                <strong>Alerts to you.</strong> Get a text on your phone when a customer accepts a quote or someone
                submits a request on your website, so you can respond fast.
              </p>
              <p className="text-gray-700">
                <strong>Texts to your customers.</strong> Send a quote or an invoice straight to a customer's phone
                with one tap. Perfect for customers who don't use email.
              </p>
            </div>
          </div>
        </div>

        {/* Alerts to you */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="text-blue-600" size={24} />
              Alerts Sent to You
            </h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Quote Accepted */}
            <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <span className="text-3xl">🎉</span>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Quote Accepted</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Get notified instantly when a customer accepts your quote and picks their service date.
                  This is your signal to call and confirm the appointment time!
                </p>
                <div className="bg-white rounded-lg p-3 border border-green-300">
                  <p className="text-xs text-gray-500 mb-1">Example message:</p>
                  <p className="text-sm font-mono text-gray-800">
                    🎉 Quote Accepted!<br /><br />
                    John Smith<br />
                    📅 Mon, Jan 15 (Morning)<br />
                    💰 $325.00<br />
                    📞 (555) 123-4567<br /><br />
                    Call to confirm time.
                  </p>
                </div>
              </div>
            </div>

            {/* New Lead */}
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-3xl">📥</span>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">New Lead</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Get notified when someone submits a quote request through your public lead form.
                  The faster you respond, the more likely you'll win the job!
                </p>
                <div className="bg-white rounded-lg p-3 border border-blue-300">
                  <p className="text-xs text-gray-500 mb-1">Example message:</p>
                  <p className="text-sm font-mono text-gray-800">
                    📥 New Lead!<br /><br />
                    Sarah Johnson<br />
                    📞 (555) 987-6543<br />
                    🔧 House Wash<br />
                    📍 Mooresville, NC<br />
                    💰 Est: $275.00<br /><br />
                    Check SuddyFlow for details.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Texts to customers */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Send className="text-green-600" size={24} />
              Texting Quotes and Invoices to Customers
            </h2>
          </div>
          <div className="p-6 space-y-6">
            <p className="text-gray-700">
              With "Text Quotes &amp; Invoices to Customers" turned on, you'll see a text button wherever you'd normally email.
              The customer gets a text with a link that opens the quote or invoice on their phone.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-gray-900 mb-1">Text Quote</h3>
                <p className="text-sm text-gray-700 mb-3">
                  On a quoted job that has a price, a service and a customer phone number, tap <strong>Text Quote</strong>.
                  The customer can view and accept the quote from the link.
                </p>
                <div className="bg-white rounded-lg p-3 border border-purple-300">
                  <p className="text-xs text-gray-500 mb-1">Example message:</p>
                  <p className="text-sm font-mono text-gray-800">
                    Hi John! Your Business sent you a quote for House Wash — $325.00. View and accept it here: [link]
                  </p>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-semibold text-gray-900 mb-1">Text Invoice</h3>
                <p className="text-sm text-gray-700 mb-3">
                  After you complete a job in Simple mode, or when you open any invoice, tap <strong>Text Invoice</strong>.
                  The customer sees the amount and status, and can pay by card if you've connected card payments.
                </p>
                <div className="bg-white rounded-lg p-3 border border-green-300">
                  <p className="text-xs text-gray-500 mb-1">Example message:</p>
                  <p className="text-sm font-mono text-gray-800">
                    Hi Sarah! Your Business sent you invoice INV-2026100001 for $175.00. View it here: [link]
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <strong>Good to know:</strong> the customer needs a phone number saved on their profile. The button only appears
              when they have one.
            </div>
          </div>
        </div>

        {/* How to Set Up */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="text-gray-600" size={24} />
              How to Set Up Text Messages
            </h2>
          </div>
          <div className="p-6">
            <ol className="space-y-4">
              {[
                ['Go to Settings → Business', 'Scroll to the "SMS Notifications" card'],
                ['Click "Edit" and enter your mobile phone number', "This is where your alerts are sent, and it's required for texting customers too"],
                ['Toggle "Enable SMS Notifications" ON', 'This is the master switch for all text messages'],
                ['Choose what you want', 'Check Quote Accepted and/or New Lead for alerts to you. Check "Text Quotes & Invoices to Customers" to text customers.'],
                ['Click "Save"', 'Your settings take effect right away'],
              ].map(([title, text], i) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">{title}</p>
                    <p className="text-sm text-gray-600">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <strong>Not seeing the Text buttons?</strong> The master switch must be on, your own mobile number must be saved,
              and "Text Quotes &amp; Invoices to Customers" must be checked.
            </div>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            💡 Pro Tips
          </h2>
          <div className="space-y-3">
            {[
              ['Respond to new leads within 5 minutes', "You're far more likely to win the job if you're the first to respond. SMS alerts help you act fast."],
              ['Call quote acceptances right away', "When someone accepts your quote, they're excited and ready. Confirm the time while it's fresh."],
              ['Text the invoice before you leave the job', 'The customer has it on their phone immediately, and it gets paid faster than one that waits in the mail.'],
              ['Email still works too', 'Text is in addition to email, not a replacement. Use whichever the customer prefers.'],
            ].map(([bold, text]) => (
              <div key={bold} className="flex items-start gap-3">
                <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  <strong>{bold}</strong> - {text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              ['Can I text my customers?',
                'Yes. You can text quotes and invoices to any customer who has a phone number on file. Turn on "Text Quotes & Invoices to Customers" in Settings → Business.'],
              ['Does this cost extra?',
                'Alerts to you are included in your SuddyFlow subscription at no additional charge.'],
              ['Will I get charged by my carrier?',
                'Standard message rates from your cell carrier may apply for alerts you receive, just like any other text.'],
              ['What phone number do the texts come from?',
                "Texts come from SuddyFlow's toll-free number. Save it as a contact so you recognize your alerts. Customers receive their quote and invoice texts from the same number."],
              ['Can a customer stop receiving texts?',
                'Yes. Customers can reply STOP to opt out of text messages.'],
              ['Can I turn off specific alerts?',
                "Yes! Each alert can be turned on or off on its own. Just uncheck the ones you don't want in Settings → Business."],
              ['The customer says they never got the text.',
                'Check that the phone number on their profile is a mobile number and is correct. Landlines can\'t receive texts. You can use Email Invoice or Download PDF instead.'],
            ].map(([q, a]) => (
              <div key={q}>
                <h3 className="font-semibold text-gray-900 mb-1">{q}</h3>
                <p className="text-sm text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Set Up Texting?</h3>
          <p className="text-gray-700 mb-4">
            It only takes about a minute to configure.
          </p>
          <button
            onClick={() => navigate('/settings?tab=business')}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Smartphone size={18} />
            Go to Settings
          </button>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmsNotificationsGuide;